import { createFileRoute } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { BackButton, Button, PrimaryButton } from "src/components/base/button";
import { useForm, useStore } from "@tanstack/react-form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { ApiInternal } from "src/api/api_internal";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import { Divider } from "src/components/base/divider";
import { Heading } from "src/components/base/heading";
import { notify } from "src/components/base/toast";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { Combobox, ComboboxOption, StatelessCombobox } from "src/components/base/combobox";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ClipboardDocumentListIcon, PlusIcon } from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import CreateTagDialog from "src/components/dialogs/create-tag";
import { Textarea } from "src/components/base/textarea";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import { TagBadge } from "src/components/badges/tag";

/**
 * The properties for {@link CreateServer}
 */
export type CreateServerProps = {};

/**
 * View for creating a new server device
 */
function CreateServer(props: CreateServerProps) {
    const [t] = useTranslation("admin-devices-create-server");
    const [tg] = useTranslation();

    const { list: tenants } = Route.useLoaderData();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);
    const accessRuleContext = React.useContext(ACCESS_RULE_CONTEXT);

    const [openCreateTag, setOpenCreateTag] = React.useState(false);
    const [tagSearch, setTagSearch] = React.useState("");
    const [tenantSearch, setTenantSearch] = React.useState("");
    const [generatedKey, setGeneratedKey] = React.useState<
        | {
              /** The preauthkey */
              key: string;
              /** The linux-cli command that includes the preauthkey */
              cliCommand: string;
          }
        | undefined
    >();

    const form = useForm({
        defaultValues: {
            ephemeral: false,
            reusable: false,
            tags: [] as Array<SimpleTag>,
            tenant: tenants.length === 1 ? tenants[0] : null,
            expiration: 7 as number | null,
            description: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.tenant === null) return { fields: { tenant: t("error.missing-tenant") } };

                const res = await ApiInternal.preAuthKeys.create({
                    ephemeral: value.ephemeral,
                    reusable: value.reusable,
                    tags: value.tags.map((tag) => tag.uuid),
                    tenant: value.tenant.uuid,
                    expiration: value.expiration,
                    description: value.description,
                });

                setGeneratedKey({
                    cliCommand: `curl -fsSL https://tailscale.com/install.sh | sh && sudo tailscale up --login-server "${settingsContext.settings.public_origin}" --auth-key=${res.key}`,
                    key: res.key,
                });
            },
        },
    });
    const appliedTags = useStore(form.store, (store) => store.values.tags);

    let filteredTags = accessRuleContext.options.tags
        .filter((tag) => !tag.locked)
        .filter((tag) => !appliedTags.some((at) => at.name === tag.name));
    filteredTags =
        tagSearch === ""
            ? filteredTags
            : filteredTags.filter((x) => x.name.toLowerCase().includes(tagSearch.toLowerCase()));

    return (
        <div className={"flex flex-col gap-6"}>
            <BackButton className={"self-start"} onClick={() => history.back()}>
                {tg("button.go-back")}
            </BackButton>

            <Form onSubmit={form.handleSubmit}>
                <Fieldset className={"max-w-xl"}>
                    <Heading>{t("heading.settings-for-new-device")}</Heading>

                    <FieldGroup>
                        <form.Field name={"tenant"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.tenant")}</RequiredLabel>
                                    <Description>{t("description.tenant")}</Description>
                                    <Combobox
                                        required
                                        autoFocus={tenants.length !== 1}
                                        value={fieldApi.state.value}
                                        onChange={(x) => fieldApi.handleChange(x)}
                                        onClose={() => setTenantSearch("")}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        queryDisplay={(q) => (q ? q.name : "")}
                                        onQueryChange={(q) => setTenantSearch(q)}
                                    >
                                        {tenants
                                            .filter((x) => x.name.toLowerCase().includes(tenantSearch.toLowerCase()))
                                            .map((tenant) => (
                                                <ComboboxOption key={tenant.uuid} value={tenant}>
                                                    <Label>{tenant.name}</Label>
                                                </ComboboxOption>
                                            ))}
                                    </Combobox>

                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                        <Divider />
                    </FieldGroup>

                    <FieldGroup>
                        <form.Field name={"ephemeral"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.ephemeral")}</Label>
                                    <Description>{t("description.ephemeral")}</Description>
                                    <CheckboxGroup>
                                        <CheckboxField>
                                            <Checkbox
                                                color={"blue"}
                                                checked={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e)}
                                            />
                                        </CheckboxField>
                                    </CheckboxGroup>
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"reusable"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.reusable")}</Label>
                                    <Description>{t("description.reusable")}</Description>
                                    <CheckboxGroup>
                                        <CheckboxField>
                                            <Checkbox
                                                color={"blue"}
                                                checked={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e)}
                                            />
                                        </CheckboxField>
                                    </CheckboxGroup>
                                </Field>
                            )}
                        </form.Field>

                        <Divider />

                        <form.Field name={"expiration"}>
                            {(fieldApi) => (
                                <>
                                    <Field>
                                        <Label>{t("label.expiration")}</Label>
                                        <Description>{t("description.expiration")}</Description>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    color={"blue"}
                                                    checked={fieldApi.state.value !== null}
                                                    onChange={(e) => fieldApi.handleChange(e ? 7 : null)}
                                                />
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                    <Field disabled={fieldApi.state.value === null}>
                                        <Label>{t("label.expiration-length")}</Label>
                                        <Description>{t("description.expiration-length")}</Description>

                                        <Input
                                            required={true}
                                            type={"number"}
                                            value={fieldApi.state.value ? fieldApi.state.value : 0}
                                            onChange={(e) => fieldApi.handleChange(parseInt(e.target.value))}
                                            min={1}
                                            max={90}
                                        />
                                    </Field>
                                </>
                            )}
                        </form.Field>

                        <Divider />

                        <form.Field name={"tags"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.forced-tags")}</Label>
                                    <Description>{t("description.forced-tags")}</Description>

                                    <div className={"mt-2 flex gap-3"}>
                                        {fieldApi.state.value.map((tag, i) => (
                                            <TagBadge
                                                key={tag.uuid}
                                                tag={tag}
                                                className={"flex gap-2!"}
                                                onRemove={() => {
                                                    const values = fieldApi.state.value;
                                                    values.splice(i, 1);
                                                    return fieldApi.handleChange([...values]);
                                                }}
                                                target={"_blank"}
                                            />
                                        ))}
                                    </div>

                                    <div className={"mt-2 flex gap-3"}>
                                        <StatelessCombobox<SimpleTag | null>
                                            onChange={(e) => {
                                                if (!e) return;

                                                if (
                                                    accessRuleContext.options.tags.some((tag) =>
                                                        tag.name.toLowerCase().includes(e.name.toLowerCase()),
                                                    )
                                                ) {
                                                    fieldApi.handleChange([...fieldApi.state.value, e]);
                                                }
                                            }}
                                            onClose={() => setTagSearch("")}
                                            onQueryChange={(q) => setTagSearch(q)}
                                        >
                                            {filteredTags.map((tag) => (
                                                <ComboboxOption value={tag} key={tag.uuid}>
                                                    <Label>{`tag:${tag.name}`}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </StatelessCombobox>

                                        <Button
                                            className={"w-full max-w-fit self-center"}
                                            onClick={() => setOpenCreateTag(true)}
                                        >
                                            <PlusIcon />
                                            {t("button.create-tag")}
                                        </Button>
                                    </div>
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"description"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.description")}</Label>
                                    <Description>{t("description.description")}</Description>
                                    <Textarea
                                        maxLength={1024}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        value={fieldApi.state.value}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <Divider />

                        <PrimaryButton type={"submit"} disabled={generatedKey !== undefined}>
                            {t("button.generate-key")}
                        </PrimaryButton>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <Suspense>
                <CreateTagDialog
                    isOpen={openCreateTag}
                    directory_uuid={undefined}
                    onClose={() => setOpenCreateTag(false)}
                    onSubmit={() => setOpenCreateTag(false)}
                />
            </Suspense>

            {generatedKey && (
                <Dialog
                    open={true}
                    onClose={() => {
                        form.reset();
                        setGeneratedKey(undefined);
                    }}
                >
                    <DialogTitle>{t("heading.pre-authentication-key-created")}</DialogTitle>
                    <DialogBody>
                        <Fieldset>
                            <FieldGroup>
                                <Field>
                                    <Label>{t("label.run-in-terminal")}</Label>
                                    <div className={"mt-2 flex gap-3"}>
                                        <div className={"rounded-lg border border-zinc-300 p-3 dark:border-zinc-700"}>
                                            <Text className={"break-all"}>{generatedKey.cliCommand}</Text>
                                        </div>
                                        <Button
                                            className={"self-start"}
                                            plain={true}
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(generatedKey?.cliCommand);
                                                notify.success(tg("toast.copied-to-clipboard"));
                                            }}
                                        >
                                            <ClipboardDocumentListIcon />
                                        </Button>
                                    </div>
                                </Field>

                                <Field>
                                    <Button
                                        outline={true}
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(generatedKey?.key);
                                            notify.success(tg("toast.copied-to-clipboard"));
                                        }}
                                    >
                                        <span>{t("button.or-copy-key")}</span>
                                        <ClipboardDocumentListIcon />
                                    </Button>
                                </Field>
                            </FieldGroup>
                        </Fieldset>
                    </DialogBody>
                    <DialogActions>
                        <Button
                            plain={true}
                            onClick={() => {
                                form.reset();
                                setGeneratedKey(undefined);
                                setTagSearch("");
                            }}
                        >
                            {t("button.create-another-device")}
                        </Button>
                        <PrimaryButton onClick={() => history.back()}>{tg("button.go-back")}</PrimaryButton>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/devices/create-server")({
    component: CreateServer,
    loader: async () => await ApiInternal.tenants.getAll(),
});
